import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import type { DateRange } from '@domain/value-objects/DateRange';
import type { RequestStatus } from '@domain/vocabulary';

/**
 * En qué mes del tablero vive cada tarjeta.
 *
 * El tablero de aprobaciones era una sola pila: con quince personas pidiendo con
 * meses de anticipación, la columna «Aprobada» tapaba a las tres que esperaban una
 * decisión hoy. Ahora una solicitud **aprobada** se archiva en el mes que se va a
 * tomar — si Moisés pidió septiembre, aparece al mirar septiembre y no antes.
 *
 * Todo lo demás se ve **siempre**, en cualquier mes, y es la mitad importante de
 * la regla: lo que el líder todavía no ha movido es trabajo suyo, y una bandeja de
 * trabajo que lo esconde por haber pasado de página es peor que una bandeja larga.
 * Lo rechazado también se queda a la vista: sus fechas no van a ocurrir, así que no
 * hay mes al que archivarlo, y se puede arrastrar de vuelta para reconsiderarlo.
 *
 * Dicho de otro modo, el mes gobierna una sola columna. Es un filtro sobre el ruido
 * —quince personas pidiendo con meses de anticipación— y no sobre el trabajo.
 */

/** Lo que necesita la función, y nada más. */
export interface BoardCard {
  readonly status: RequestStatus;
  readonly range: DateRange;
}

/** Si la solicitud se archiva por sus fechas en vez de quedarse en hoy. */
export const filesByAbsenceMonth = (status: RequestStatus): boolean => status === 'Aprobada';

/** Si dos fechas caen en el mismo mes del mismo año. */
export const sameMonth = (a: CalendarDate, b: CalendarDate): boolean =>
  a.year === b.year && a.month === b.month;

/**
 * Si la tarjeta se ve en el mes que se está mirando.
 *
 * Una ausencia aprobada aparece en **todos** los meses que toca, no sólo en el de
 * su primer día: quien mira septiembre para saber quién falta necesita ver al que
 * se fue el 28 de agosto y vuelve el 5 de septiembre. Archivarla sólo por el mes
 * de inicio la habría escondido justo en el mes en que esa persona no está.
 *
 * Lo que no está aprobado no depende del mes: se ve siempre.
 */
export const belongsToBoardMonth = (card: BoardCard, viewing: CalendarDate): boolean => {
  if (!filesByAbsenceMonth(card.status)) return true;

  const asMonths = (date: CalendarDate): number => date.year * 12 + date.month;
  const viewed = asMonths(viewing);

  return asMonths(card.range.start) <= viewed && viewed <= asMonths(card.range.end);
};
